import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { ErrorMessage, Field, Form, Formik } from 'formik';

// COMPONENTS
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ErrorMes from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Button from '../../../components/Button';
import OrganisationLogo from '../../../components/OrganisationLogo';

// TYPES
import {
	DomainProfileType,
	OrganisationSearchOrganisationSearchSummary,
	AccountJoinOrganisationCommand
} from '../../../api/models';

interface CertificationStudentTypes {
	organisationData?: OrganisationSearchOrganisationSearchSummary;
	profileData?: { label?: string; value?: string } | undefined;
}

const CertificationStudent: React.FC<CertificationStudentTypes> = ({ organisationData, profileData }) => {
	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [success, setSuccess] = React.useState<boolean>(false);

	const doJoinOrganisation = async (values: any) => {
		setIsLoading(true);

		const data: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: DomainProfileType.Student,
			parentsNames: values.parentName,
			parentsPhoneNumber: values.parentPhone
		};

		const response = await apibridge.doJoinOrganisation(data);

		if (response.isError) {
			setIsLoading(false);

			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setShowError(false);

			setIsLoading(false);

			setSuccess(true);
		}
	};

	// Initial Values
	const initialChildValues = {
		parentName: '',
		parentPhone: '',
		acknowledge: false
	};

	// FORM VALIDATION FOR AUSTRALIAN PHONE NUMBERS
	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	//Validation
	const validation = Yup.object({
		parentName: Yup.string().required('Required'),
		parentPhone: Yup.string().matches(phoneRegExp, 'Phone number is not valid').required('Required'),
		acknowledge: Yup.boolean().required('Required').oneOf([true], 'You must agree to continue')
	});

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMes message={formErrorMessage} />
			) : success ? (
				<CertificationSuccess organisationName={organisationData && organisationData.name} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{!success ? (
									<>
										<div className="header row">
											<div className="section-header col-12">
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">Joining {organisationData?.name} as a Child or Student</h2>

												<p className="sub-grey-text">Please provide additional information.</p>
											</div>
										</div>

										<Formik
											initialValues={initialChildValues}
											validationSchema={validation}
											onSubmit={doJoinOrganisation}
										>
											{(formik) => {
												return (
													<Form translate="yes">
														<div className="form-group">
															<label htmlFor="parentName">Add a parent’s full name</label>

															<Field className="form-control" name="parentName" type="text" />

															<ErrorMessage name="parentName" component="div" className="form-validation-error" />
														</div>

														<div className="form-group">
															<label htmlFor="parentPhone">Add a parent’s mobile phone number</label>

															<Field className="form-control" name="parentPhone" type="text" />

															<ErrorMessage name="parentPhone" component="div" className="form-validation-error" />
														</div>

														<div className="general-divider"></div>

														<div className="form-group acknowledge-container">
															<label className="form-control-checkbox">
																<Field
																	name="acknowledge"
																	onChange={() => formik.setFieldValue('acknowledge', !formik.values.acknowledge)}
																	type="checkbox"
																/>

																<span>
																	I have obtained permission from my parent or legal guardian to access and use the
																	ChildAware Platform and they have read with me the Individual User{' '}
																	<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
																		Terms and Conditions
																	</a>{' '}
																	and the{' '}
																	<a href={`${websiteUrl}/privacy-policy`} target="_blank">
																		Privacy Policy
																	</a>{' '}
																	which describe how my personal information may be handled by ChildAware.
																</span>
															</label>

															<ErrorMessage name="acknowledge" component="div" className="form-validation-error" />
														</div>

														<Button className="btn btn-primary full-width" disabled={isLoading} type="submit">
															Continue
														</Button>
													</Form>
												);
											}}
										</Formik>
									</>
								) : (
									<CertificationSuccess organisationName={organisationData && organisationData.name} />
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationStudent;
